import React, {
    HTMLInputTypeAttribute,
    InputHTMLAttributes,
    ReactElement,
    ReactNode,
} from "react";

// eslint-disable-next-line import/no-extraneous-dependencies
import { RiCheckFill } from "@remixicon/react";
import classNames from "classnames";
import Popover from "../../popover/Popover";
import useOrCreateRef from "../../useOrCreateRef";
import useAutoFocus from "./useAutoFocus";
import { HtmlRenderer } from "../../html-render/HtmlRenderer";

export const SIZES = ["filter", "small", "medium"] as const;
type SizeType = typeof SIZES[number];
const SIZE_CLASS: Record<SizeType, string> = {
    filter: "form-control-filter",
    small: "form-control-sm",
    medium: "",
    // large: "form-control-lg",
};

export const VARIANT = ["default", "danger", "success"] as const;
type VariantType = typeof VARIANT[number];

export const HELP_DISPLAY = ["default", "icon"] as const;
type HelpDisplayType = typeof HELP_DISPLAY[number];

export interface FormGroupProps extends Omit<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, "size"> {
    isRequired?: boolean;
    isDisabled?: boolean;
    label?: ReactNode;
    isOptional?: boolean;
    helpText?: ReactNode;
    groupClassName?: string;
    size?: SizeType;
    variant?: VariantType;
    type?: HTMLInputTypeAttribute;
    prefixIcon?: ReactNode;
    postfixIcon?: ReactNode;
    display?: "input" | "textarea";
    helpDisplay?: HelpDisplayType;
    children?: ReactNode;
}

const FormGroup = React.forwardRef((
    args: FormGroupProps,
    ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
): ReactElement => {
    const {
        isRequired,
        label,
        helpText,
        className,
        size,
        variant,
        isDisabled,
        type,
        groupClassName,
        prefixIcon,
        display,
        isOptional,
        postfixIcon,
        children,
        helpDisplay,
        autoFocus,
        ...rest
    } = args;

    const inputRef = useOrCreateRef<HTMLInputElement | HTMLTextAreaElement>(ref);
    const sizeClass = size ? SIZE_CLASS[size] : "";

    useAutoFocus<HTMLInputElement | HTMLTextAreaElement>({ ref: inputRef, shouldAutoFocus: autoFocus });

    let inputElement = children;
    if (!inputElement) {
        inputElement = display === "input" ? (
            <input
                ref={inputRef as React.Ref<HTMLInputElement>}
                required={isRequired}
                className={`form-control ${sizeClass} ${className}`}
                disabled={isDisabled}
                type={type}
                {...rest}
            />
        ) : (
            <textarea
                ref={inputRef as React.Ref<HTMLTextAreaElement>}
                required={isRequired}
                className={`form-control ${sizeClass} ${className}`}
                disabled={isDisabled}
                {...rest}
            />
        );
    }

    return (
        <div className={classNames(
            "form-group",
            variant ? `form-group-${variant}` : "",
            groupClassName,
            children && isRequired ? "form-group-required" : "",
        )}
        >
            {label && (
                <label className="form-label">
                    {label}
                    {isOptional ? (
                        <span className="form-label-optional">
                            {" "}
                            (Optional)
                        </span>
                    ) : ""}
                    {helpText && helpDisplay === "icon" ? (
                        <>
                            {" "}
                            <Popover size="md" iconSize="xs" variant="tooltip" buttonClassName="form-control-help">
                                {helpText}
                            </Popover>
                        </>
                    ) : ""}
                </label>
            )}

            {(variant === "success" || prefixIcon || postfixIcon) ? (
                <div className="input-group">
                    {prefixIcon && (
                        <span className="input-group-text input-group-icon input-group-prefix">
                            {prefixIcon}
                        </span>
                    )}
                    {inputElement}
                    {variant === "success" && (
                        <span className="input-group-text form-success-icon">
                            <RiCheckFill />
                        </span>
                    )}
                    {postfixIcon && (
                        <span className="input-group-text input-group-icon input-group-postfix">
                            {postfixIcon}
                        </span>
                    )}

                </div>
            ) : inputElement}

            {helpText && helpDisplay === "default" && (
                <div className="form-text">
                    {typeof helpText === "string"
                        ? <HtmlRenderer html={helpText} />
                        : helpText}
                </div>
            )}

        </div>
    );
});

FormGroup.defaultProps = {
    groupClassName: "",
    className: "",
    isOptional: false,
    display: "input",
    children: undefined,
    helpDisplay: "default",
    type: "text",
};

export default FormGroup;
